import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useGetMasterWithSubsQuery, useUpdateProjectMutation } from 'features/master/api/master.api';
import { MasterDetail, ProjectDetail } from 'features/master/api/types';
import { BookOpen, FunctionSquare, GitCompare, Plus, Save, Sparkles, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { useGetSalesPromptRewriteSubProjectsQuery } from '../api/salesPromptRewrite.api';
import {
  PromptType,
  PromptVersion,
  useGetAgentModelOptionsQuery,
  useListPromptVersionsQuery,
} from '../services/projects.api';
import PromptDiffDialog from './PromptDiffDialog';
import PromptFunctionsPanel from './PromptFunctionsPanel';
import PromptSectionsPanel from './PromptSectionsPanel';
import PublishVersionDialog, { PublishableLabel } from './PublishVersionDialog';
import SalesPromptRewritePanel from './SalesPromptRewritePanel';

const VARIABLE_REGEX = /<([A-Z_][A-Z0-9_]*(?::[A-Za-z0-9_]+)?)>/g;

const detectVariables = (content: string): string[] => {
  const set = new Set<string>();
  for (const match of content.matchAll(VARIABLE_REGEX)) {
    set.add(match[1]);
  }
  return Array.from(set);
};

const findSubProject = (master: MasterDetail | undefined, subProjectId: number): ProjectDetail | null => {
  if (!master?.subProjects?.length) return null;
  return master.subProjects.find((sub) => sub.id === subProjectId) ?? null;
};

const PromptVersionView = (): JSX.Element => {
  const { masterProjectId, subProjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const masterId = masterProjectId !== undefined ? Number(masterProjectId) : null;
  const subId = subProjectId !== undefined ? Number(subProjectId) : null;
  const isMasterRoute = subId === null;

  const promptBaseUrl = isMasterRoute ? `/project/${masterId}/prompt` : `/project/${masterId}/prompt/${subId}`;

  const incomingBaseVersion = (location.state as { baseVersion?: PromptVersion } | null)?.baseVersion ?? null;

  const { data: master, isLoading: isMasterLoading } = useGetMasterWithSubsQuery(masterId ?? 0, {
    skip: masterId === null,
  });

  const subProject = !isMasterRoute && master && subId !== null ? findSubProject(master, subId) : null;
  const project: ProjectDetail | MasterDetail | null | undefined = isMasterRoute ? master : subProject;
  const promptType: PromptType = isMasterRoute ? 'common' : 'subproject';
  const queryProjectId = isMasterRoute ? masterId : subId;

  // Always fetch the newest version so we know the real next version number.
  // The chain is linear: publishing appends at max(version) + 1 regardless of
  // which version we based the draft on.
  const { data: page } = useListPromptVersionsQuery(
    { projectId: queryProjectId ?? 0, promptType, limit: 1, offset: 0 },
    { skip: queryProjectId === null }
  );

  const latestVersionNumber = page?.items[0]?.version ?? 0;
  const baseVersion = incomingBaseVersion ?? page?.items[0] ?? null;

  const { data: modelOptions } = useGetAgentModelOptionsQuery(queryProjectId ?? 0, {
    skip: queryProjectId === null,
  });
  // The publish dialog starts from the base version's model test, so CS keeps testing the same model while iterating
  // on a latest version. Any other version starts from the production model.
  const baseModel = baseVersion?.isModelTest ? baseVersion.agentAIModel : modelOptions?.currentModel;
  // A master model covers every subproject, so applying it moves all of them.
  const movesSubprojects = isMasterRoute && !!master?.subProjects?.length;

  const [content, setContent] = useState('');
  const hasInitialized = useRef(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [functionsOpen, setFunctionsOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);

  const { data: rewriteTargets } = useGetSalesPromptRewriteSubProjectsQuery(
    { masterProjectId: masterId ?? 0, subProjectId: subId ?? undefined },
    { skip: masterId === null || isMasterRoute }
  );
  const rewriteTarget = rewriteTargets?.find((sub) => sub.id === subId);
  const rewriteDisabledReason = isMasterRoute
    ? project?.commonPrompt
      ? null
      : 'no tener prompt común'
    : !rewriteTarget
      ? 'carga en curso'
      : !rewriteTarget.hasPrompt
        ? 'no tener prompt propio'
        : !rewriteTarget.hasAdvisor
          ? 'no tener orientador'
          : null;
  const canRewrite = rewriteDisabledReason === null;

  useEffect(() => {
    if (hasInitialized.current || !project) return;
    const initial = baseVersion?.content ?? (isMasterRoute ? project.commonPrompt : project.prompt) ?? '';
    setContent(initial);
    hasInitialized.current = true;
  }, [baseVersion, isMasterRoute, project]);

  const [updateProject, { isLoading: isPublishing }] = useUpdateProjectMutation();

  const deferredContent = useDeferredValue(content);
  const variables = useMemo(() => detectVariables(deferredContent), [deferredContent]);
  const hasChanges = content !== (baseVersion?.content ?? '');

  const handlePublish = async ({
    changeReason,
    promptLabel,
    model,
  }: {
    changeReason: string;
    promptLabel: PublishableLabel;
    model: string | null;
  }): Promise<void> => {
    if (!project) return;
    const promptField = isMasterRoute ? { commonPrompt: content } : { prompt: content };
    await updateProject({
      id: project.id,
      patch: { ...promptField, changeReason, promptLabel, ...(model && { promptModel: model }) },
    }).unwrap();
    setIsPublishOpen(false);
    setIsReviewOpen(false);
    navigate(promptBaseUrl);
  };

  if (isMasterLoading || !project) {
    return (
      <section className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-[600px] w-full" />
      </section>
    );
  }

  const title = isMasterRoute ? 'Prompt Común' : project.name;
  const eyebrow = isMasterRoute ? 'PROMPT BASE' : 'PROMPT SUBPROYECTO';
  const nextVersionLabel = `v${latestVersionNumber + 1}`;

  return (
    <section className="flex h-full min-h-[640px] flex-col overflow-hidden rounded-xl border border-border-subtle bg-bg-surface">
      <header className="flex items-center justify-between gap-4 border-b border-border-subtle bg-white px-6 py-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-[11px] font-medium uppercase tracking-widest text-text-subtle">{eyebrow}</span>
          <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
            <span className="shrink-0 text-text-default font-semibold">{title}</span>
            <span className="text-text-subtle">/</span>
            <span className="shrink-0 text-text-muted">Nueva versión {nextVersionLabel}</span>
            <Badge className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100">
              Borrador
            </Badge>
            {baseVersion && (
              <span className="truncate text-[11px] text-text-subtle">
                · ↳ basada en prompt v{baseVersion.version}
                {baseVersion.label ? ` ${baseVersion.label}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!hasChanges}
            onClick={() => setIsReviewOpen(true)}
          >
            Revisar cambios
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!hasChanges || isPublishing}
            onClick={() => setIsPublishOpen(true)}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            Publicar versión
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => navigate(promptBaseUrl)}>
            Descartar
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border-subtle bg-gray-50 px-6 py-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">Variables</span>
            {variables.length === 0 && <span className="text-[11px] text-text-subtle">Sin variables</span>}
            {variables.map((variable) => (
              <Badge
                key={variable}
                variant="outline"
                className="border-indigo-200 bg-indigo-50 font-mono text-indigo-700"
              >
                {variable}
              </Badge>
            ))}
          </div>

          <Textarea
            id="prompt-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-0 flex-1 resize-none rounded-none border-0 bg-gray-50 p-4 font-mono text-xs leading-relaxed text-text-default shadow-none focus-visible:ring-0 [field-sizing:fixed]"
            placeholder="Escribe el contenido del prompt..."
          />
        </div>
        {canRewrite && masterId !== null && (
          <aside className={cn('w-[440px] shrink-0 border-l border-border-subtle bg-white', !rewriteOpen && 'hidden')}>
            <SalesPromptRewritePanel
              key={`${masterId}-${subId ?? 'common'}`}
              masterProjectId={masterId}
              subProjectId={subId}
              onUsePrompt={setContent}
            />
          </aside>
        )}
      </div>

      {/* FAB */}
      <div className="pointer-events-none fixed bottom-8 right-6 z-50 flex flex-col items-end gap-3">
        {fabOpen && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="pointer-events-auto">
                  <button
                    type="button"
                    disabled={!canRewrite}
                    onClick={() => {
                      setRewriteOpen((prev) => !prev);
                      setFabOpen(false);
                    }}
                    className={cn('flex items-center gap-3', !canRewrite && 'pointer-events-none opacity-50')}
                  >
                    <span className="rounded-full bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow-md">
                      {rewriteOpen ? 'Ocultar orientador' : 'Adaptar al orientador'}
                    </span>
                    <span className="flex size-11 items-center justify-center rounded-full bg-gray-700 shadow-md hover:bg-gray-600">
                      <Sparkles className="size-5 text-white" />
                    </span>
                  </button>
                </span>
              </TooltipTrigger>
              {rewriteDisabledReason && (
                <TooltipContent side="left">Deshabilitado por {rewriteDisabledReason}</TooltipContent>
              )}
            </Tooltip>
            <button
              type="button"
              onClick={() => {
                setSectionsOpen(true);
                setFabOpen(false);
              }}
              className="pointer-events-auto flex items-center gap-3"
            >
              <span className="rounded-full bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow-md">
                Secciones del prompt
              </span>
              <span className="flex size-11 items-center justify-center rounded-full bg-gray-700 shadow-md hover:bg-gray-600">
                <BookOpen className="size-5 text-white" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setFunctionsOpen(true);
                setFabOpen(false);
              }}
              className="pointer-events-auto flex items-center gap-3"
            >
              <span className="rounded-full bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow-md">
                Funciones adicionales
              </span>
              <span className="flex size-11 items-center justify-center rounded-full bg-gray-700 shadow-md hover:bg-gray-600">
                <FunctionSquare className="size-5 text-white" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsReviewOpen(true);
                setFabOpen(false);
              }}
              disabled={!hasChanges}
              className={cn(
                'pointer-events-auto flex items-center gap-3',
                !hasChanges && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="rounded-full bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow-md">
                Revisar cambios
              </span>
              <span className="flex size-11 items-center justify-center rounded-full bg-gray-700 shadow-md hover:bg-gray-600">
                <GitCompare className="size-5 text-white" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsPublishOpen(true);
                setFabOpen(false);
              }}
              disabled={!hasChanges || isPublishing}
              className={cn(
                'pointer-events-auto flex items-center gap-3',
                (!hasChanges || isPublishing) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="rounded-full bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow-md">
                Publicar versión
              </span>
              <span className="flex size-11 items-center justify-center rounded-full bg-gray-700 shadow-md hover:bg-gray-600">
                <Save className="size-5 text-white" />
              </span>
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setFabOpen((prev) => !prev)}
          aria-label={fabOpen ? 'Cerrar menú de acciones' : 'Abrir menú de acciones'}
          aria-expanded={fabOpen}
          className="pointer-events-auto flex size-14 items-center justify-center rounded-full bg-blue-600 shadow-lg transition hover:bg-blue-500"
        >
          {fabOpen ? <X className="size-6 text-white" /> : <Plus className="size-6 text-white" />}
        </button>
      </div>

      {/* Side sheets */}
      <Sheet open={sectionsOpen} onOpenChange={setSectionsOpen}>
        <SheetContent side="right" className="w-[540px] p-0 sm:max-w-[540px]">
          <SheetHeader className="sr-only">
            <SheetTitle>Secciones del prompt</SheetTitle>
          </SheetHeader>
          {queryProjectId !== null && <PromptSectionsPanel projectId={queryProjectId} />}
        </SheetContent>
      </Sheet>

      <Sheet open={functionsOpen} onOpenChange={setFunctionsOpen}>
        <SheetContent side="right" className="w-[400px] p-0 sm:max-w-[400px]">
          <SheetHeader className="sr-only">
            <SheetTitle>Funciones adicionales</SheetTitle>
          </SheetHeader>
          <PromptFunctionsPanel functions={project?.openAIFunctions ?? []} />
        </SheetContent>
      </Sheet>

      {isPublishOpen && (
        <PublishVersionDialog
          open
          onOpenChange={setIsPublishOpen}
          subtitle={`${title} · ${nextVersionLabel}`}
          isPublishing={isPublishing}
          modelOptions={modelOptions}
          defaultModel={baseModel}
          movesSubprojects={movesSubprojects}
          onPublish={handlePublish}
        />
      )}

      {isReviewOpen && (
        <PromptDiffDialog
          open
          onOpenChange={setIsReviewOpen}
          title="Revisar cambios"
          subtitle={`${title} · ${nextVersionLabel} Borrador`}
          oldContent={baseVersion?.content ?? ''}
          newContent={content}
          cancelLabel="Cancelar"
          primaryAction={{
            label: 'Publicar versión',
            disabled: !hasChanges || isPublishing,
            onClick: () => {
              setIsReviewOpen(false);
              setIsPublishOpen(true);
            },
          }}
        />
      )}
    </section>
  );
};

export default PromptVersionView;
